import React, { PropsWithChildren, useRef, useState, ReactNode } from "react";
import { ReactComponent as ArrowDownIcon } from "assets/icons/arrow-down.svg";
import { useOutsideClick } from "modules/common";
import { useElementRendered } from "helpers/DomHelper";

interface Props {
    name: string;
    label: string;
    className?: string;
    onItemClick?: () => void;
    children: ReactNode;
}

export const NavDropdown: React.FC<PropsWithChildren<Props>> = ({
    className,
    name,
    label,
    children,
    onItemClick,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const firstChildRef = useRef<HTMLElement>(null);
    const [show, setShow] = useState(false);

    useOutsideClick(ref, () => setShow(false));

    const handleItemClick = () => {
        setShow(false);
        onItemClick?.();
    };

    const dropdownClass = `nav-${name}`;
    const hasActiveItem = useElementRendered(`.${dropdownClass} .nav__item--active`);

    const handleKeyDownTitle = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShow(true);

            setTimeout(() => {
                firstChildRef.current?.focus();
            }, 0);
        }
    };

    return (
        <div
            className={`${dropdownClass} nav-dropdown${
                hasActiveItem ? " nav-dropdown--active" : ""
            }`}
            ref={ref}
        >
            <div
                className={`nav-dropdown__title ${className ?? ""}`}
                onKeyDown={handleKeyDownTitle}
                tabIndex={0}
                role="button"
                aria-expanded={show}
                aria-haspopup="true"
                onClick={() => setShow(!show)}
            >
                <span className="nav-dropdown__text">{label}</span>
                <ArrowDownIcon className={`${show && "nav-dropdown__icon--active"}`} />
            </div>

            <div
                className={`nav-dropdown__container${show ? "" : " nav-dropdown--hidden"}`}
                onClick={handleItemClick}
                onBlur={e => {
                    if (!ref.current?.contains(e.relatedTarget as Node)) {
                        setShow(false);
                    }
                }}
            >
                {React.Children.map(children, (child, index) => {
                    if (!React.isValidElement(child)) return child;

                    if (index === 0) {
                        return React.cloneElement(child as React.ReactElement<any>, {
                            ref: firstChildRef,
                        });
                    }

                    return child;
                })}
            </div>
        </div>
    );
};
